import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Check, X, Pencil, Undo2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { actionSetFor } from '@/hooks/workbench/statusActions';
import type { WorkbenchStatus } from '@/hooks/workbench/statusScope';

interface WorkbenchActionBarProps {
  canGoPrev: boolean;
  canGoNext: boolean;
  isAccepting: boolean;
  isRejecting: boolean;
  isCurrentReviewed: boolean;
  /** Der aktive Statusreiter. Bestimmt, welche zwei Handlungen hier stehen. */
  status?: WorkbenchStatus;
  onPrev: () => void;
  onNext: () => void;
  onAccept: () => void;
  onReject: () => void;
  /** Reiter "Freigegeben" und "Abgelehnt": springt in den Bearbeiten-Modus. */
  onEdit?: () => void;
  /** Reiter "Freigegeben": zurueck in die Pruefung, mit Rueckfrage. */
  onWithdrawApproval?: () => void;
  /** Reiter "Abgelehnt": zurueck in die Pruefung, ohne Rueckfrage. */
  onReopen?: () => void;
  /**
   * Die letzte Entscheidung DIESER SITZUNG zuruecknehmen (Spec 4.4).
   *
   * Sie muss gemerkt werden, weil Freigeben und Ablehnen sofort zum naechsten
   * Produkt weiterspringen: das "aktuelle" Produkt ist danach nicht mehr das
   * entschiedene. Fehlt der Handler, gibt es den Knopf gar nicht.
   */
  onUndoLast?: () => void;
  /** Gibt es ueberhaupt etwas zurueckzunehmen? */
  canUndoLast?: boolean;
}

export function WorkbenchActionBar({
  canGoPrev,
  canGoNext,
  isAccepting,
  isRejecting,
  isCurrentReviewed,
  status = 'pending',
  onPrev,
  onNext,
  onAccept,
  onReject,
  onEdit,
  onWithdrawApproval,
  onReopen,
  onUndoLast,
  canUndoLast,
}: WorkbenchActionBarProps) {
  const { t } = useTranslation('workbench');
  const actions = actionSetFor(status);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const busy = isAccepting || isRejecting;

  /**
   * Die beiden Knoepfe des aktiven Reiters. Welche Handlung an welchem Platz
   * steht, kommt aus `statusActions` — dieselbe Quelle, aus der der Reviewer
   * ableitet, ob sein Tastatur-Handler ueberhaupt greifen darf. Zwei getrennte
   * Verzweigungen waeren genau die Doppelung, an der eine Tastatursperre schon
   * einmal nur die Knoepfe erwischt hat.
   */
  const renderAction = (which: 'primary' | 'secondary') => {
    const action = actions[which];

    if (action === 'accept') {
      // Der Haftungssatz stand als zweizeiliger Text unter dem Knopf und hat
      // die Leiste auf 96px getrieben; danach nur noch als Info-Popover
      // daneben. Beides ist entfallen (T1): der Rechtstext steht seitdem als
      // fuenfter Eintrag mit eigenem Dialog in WorkbenchReleaseMenu - EIN
      // Fundort statt zwei, die sonst irgendwann auseinanderlaufen.
      // `actions.disclaimerAria` bleibt als Schluessel im Bestand, wird hier
      // aber nicht mehr vergeben.
      return (
        <Button
          variant="default"
          size="sm"
          className="bg-success hover:bg-success-hover text-success-foreground"
          onClick={onAccept}
          disabled={busy || isCurrentReviewed}
        >
          <Check className="h-4 w-4 mr-1.5" />
          {isAccepting ? t('actions.accepting') : t('actions.accept')}
          {/* T6.2 (08.09.2026): unter 1024px ausgeblendet. `aria-hidden`
              macht den Hinweis ohnehin rein optisch - unter `lg` ist die
              Tastatur nicht der Bedienweg, und er war der Grund, aus dem
              die Zeile bei 1024px umbrach. Die Belegung selbst bleibt. */}
          <span aria-hidden="true" className="ml-1.5 hidden text-[10px] lg:inline">
            ({t('actions.shortcutAccept')})
          </span>
        </Button>
      );
    }

    if (action === 'reject') {
      return (
        /* `text-destructive` ist ein FLAECHEN-Token. Als Schriftfarbe auf
           der Karte gemessen 3.58:1 - der Knopf, der eine Optimierung
           verwirft, war der kontrastschwaechste Text der ganzen Leiste. Der
           Rand darf die Flaechenfarbe behalten, Raender sind keine Textflaeche
           (AA 1.4.3 gilt fuer Text), und der Umkehr-Zustand beim Ueberfahren
           setzt ohnehin `destructive-foreground` auf volle Flaeche. */
        <Button
          variant="outline"
          size="sm"
          className="border-destructive text-destructive-ink hover:bg-destructive hover:text-destructive-foreground"
          onClick={onReject}
          disabled={busy || isCurrentReviewed}
        >
          <X className="h-4 w-4 mr-1.5" />
          {isRejecting ? t('actions.rejecting') : t('actions.reject')}
          {/* Selbe Begruendung wie beim Accept-Knopf oben. */}
          <span aria-hidden="true" className="ml-1.5 hidden text-[10px] lg:inline">
            ({t('actions.shortcutReject')})
          </span>
        </Button>
      );
    }

    if (action === 'edit') {
      // K8-Befund (wie unten bei reopen): die default-Variante traegt
      // Weiss auf Indigo (2.95:1 dunkel). Der Primaerknopf des Reiters
      // bekommt dieselbe success-Flaeche wie alle Werkbank-Primaerknoepfe.
      return (
        <Button
          variant={which === 'primary' ? 'default' : 'outline'}
          className={which === 'primary' ? 'bg-success hover:bg-success-hover text-success-foreground' : undefined}
          onClick={onEdit}
          disabled={busy}
        >
          <Pencil className="h-4 w-4 mr-1.5" />
          {t('actions.edit')}
        </Button>
      );
    }

    if (action === 'reopen') {
      return (
        <Button
          className="bg-success hover:bg-success-hover text-success-foreground"
          onClick={onReopen}
          disabled={busy}
        >
          <RotateCcw className="h-4 w-4 mr-1.5" />
          {t('actions.reopen')}
        </Button>
      );
    }

    // withdrawApproval — die einzige Handlung mit Rueckfrage. Sie ist der Weg
    // fuer den Versehensfall; der Normalweg fuer eine freigegebene Fassung
    // ist Bearbeiten.
    return (
      <>
        <Button variant="outline" onClick={() => setConfirmOpen(true)} disabled={busy}>
          <Undo2 className="h-4 w-4 mr-1.5" />
          {t('actions.withdrawApproval')}
        </Button>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent
            onCloseAutoFocus={() => {
                        /* V3-Befund: Radix gibt den Fokus an das zuvor fokussierte
                           Element zurueck — das kann durch die BESTAETIGTE Handlung
                           verschwunden sein (letztes Produkt des Reiters, geleerte
                           Auswahl). Dann faellt der Fokus auf <body> und Tab beginnt
                           wieder ganz oben. Nach dem Standardverhalten pruefen, und
                           falls nichts angekommen ist, auf den aktiven Reiter setzen:
                           der existiert immer. */
                        queueMicrotask(() => {
              /* Microtask statt setTimeout: ein Timer kann den Unmount (und
                 in Tests den Abbau der DOM-Umgebung) ueberleben — genau das
                 hat den CI-Lauf mit "document is not defined" gerissen,
                 waehrend die Suite lokal nur durch Timing gruen war. Die
                 Microtask laeuft noch im selben Task, direkt nach Radix'
                 eigener Fokus-Rueckgabe, und kann nichts ueberleben. */
              if (typeof document === 'undefined') return;
              if (document.activeElement === document.body) {
                document
                  .querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
                  ?.focus();
              }
            });
                      }}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>{t('withdrawConfirm.title')}</AlertDialogTitle>
              <AlertDialogDescription>{t('withdrawConfirm.description')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('withdrawConfirm.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setConfirmOpen(false);
                  onWithdrawApproval?.();
                }}
              >
                {t('actions.withdrawConfirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  };

  return (
    /* Die Leiste sitzt am Fuss DER KARTE. Vorher klebte sie mit
       `position: fixed` am Fensterrand, war zweizeilig 152 bis 165 px hoch
       und zwang die Seite, ebensoviel Platz freizuhalten - Platz, der genau
       der Flaeche fehlte, auf der entschieden wird.

       `min-h-[52px]` statt einer festen Hoehe: bei sehr schmalen Fenstern
       darf die Reihe umbrechen, statt die Trefferflaechen der Pfeile von
       40 px herunterzuquetschen. */
    <div
      className="flex min-h-[52px] flex-shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-muted/20 px-3 py-2"
      data-testid="workbench-action-bar"
    >
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0"
        onClick={onPrev}
        disabled={!canGoPrev}
        aria-label={t('actions.prev')}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <div
        className="order-first flex basis-full items-center justify-center gap-2 sm:order-none sm:flex-1 sm:basis-auto"
        data-testid="workbench-action-bar-actions"
      >
        {/* Zweite Handlung links, erste rechts - dieselbe Anordnung wie
            vorher, damit die gewohnte Handbewegung im Reiter "Offen"
            unveraendert bleibt. */}
        {renderAction('secondary')}
        {renderAction('primary')}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {onUndoLast && (
          <Button
            variant="ghost"
            size="sm"
            data-testid="workbench-undo-last"
            aria-label={t('actions.undoLastAria')}
            className="h-8 px-2 text-xs text-muted-foreground"
            onClick={onUndoLast}
            disabled={!canUndoLast || busy}
          >
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
            {t('actions.undoLast')}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={onNext}
          disabled={!canGoNext}
          aria-label={t('actions.next')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
